import { v4 as uuidv4 } from "uuid";
import { client } from "../index";
import { TRACK_BOOKING_FUNNEL_STEP } from "../graphql/bookings/trackFunnelStepMutation";

// NU-12910: fire-and-forget booking-funnel drop-off tracking for the
// website. Deliberately no loading state, no retry/offline-queue, and every
// error is swallowed - this must never be visible to the customer or slow
// down the booking flow it's observing. See the canonical stage enum this
// maps into: reservation-api's BookingFunnelStage.
export enum FunnelStage {
  LANDING = "LANDING",
  VEHICLE_SEARCH = "VEHICLE_SEARCH",
  VEHICLE_SELECTED = "VEHICLE_SELECTED",
  ADDONS_SELECTION = "ADDONS_SELECTION",
  PERSONAL_DETAILS_VIEWED = "PERSONAL_DETAILS_VIEWED",
  QUOTE_VIEW = "QUOTE_VIEW",
  PAYMENT_VIEW = "PAYMENT_VIEW",
  NEED_HELP_OPENED = "NEED_HELP_OPENED"
}

export enum FunnelFlow {
  RENTAL = "RENTAL",
  SUBSCRIPTION = "SUBSCRIPTION",
  TEST_DRIVE = "TEST_DRIVE"
}

const SESSION_STORAGE_KEY = "coastr_funnel_session_id";

export function getOrCreateFunnelSessionId(): string {
  try {
    let sessionId = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = uuidv4();
      window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch (error) {
    // localStorage can throw in private-browsing/disabled-storage contexts -
    // fall back to an in-memory id for the life of this page load rather
    // than breaking the caller.
    return uuidv4();
  }
}

interface ITrackFunnelStepContext {
  branchId?: string;
  vehicleGroupId?: string;
  vehicleGroupName?: string;
  pickupServiceLocation?: string;
  pickupDateTime?: string;
  dropoffDateTime?: string;
}

// `enabled` is the org's webAppSetting.enableUserDropoffTrackingWebsite flag
// - callers pass it through explicitly rather than this module reaching
// into redux, so it stays trivially testable and has no store dependency.
export function trackFunnelStep(
  enabled: boolean | undefined,
  flow: FunnelFlow,
  stage: FunnelStage,
  context?: ITrackFunnelStepContext
): void {
  if (!enabled) {
    return;
  }
  try {
    const sessionId = getOrCreateFunnelSessionId();
    client
      .mutate({
        mutation: TRACK_BOOKING_FUNNEL_STEP,
        variables: {
          input: {
            sessionId,
            channel: "WEB",
            flow,
            stage,
            platform: "web",
            ...context
          }
        }
      })
      .catch(() => {
        // Intentionally swallowed - see module comment.
      });
  } catch (error) {
    // Intentionally swallowed - see module comment.
  }
}
